// src/firebase.js
import { initializeApp } from "firebase/app";
import { getFirestore, serverTimestamp } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getAuth, signInAnonymously, onAuthStateChanged, setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  onTokenChanged,
  getToken,
} from "firebase/app-check";

// --- Config Firebase (lee de .env) ---
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET, // <- firebasestorage.app
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// --- Core Firebase ---
const app = initializeApp(firebaseConfig);

export const db = getFirestore(app);
export const storage = getStorage(app); // usa el bucket por defecto de firebaseConfig
export const auth = getAuth(app);
export { serverTimestamp };

// 🔒 Persistencia solo por sesión: al cerrar el navegador/pestaña se cierra sesión
setPersistence(auth, browserSessionPersistence)
  .then(() => {
    console.log("[Auth] ✅ Persistencia configurada: browserSessionPersistence");
  })
  .catch((error) => {
    console.error("[Auth] ❌ Error configurando persistencia:", error);
  });

console.log("[Firebase] bucket:", firebaseConfig.storageBucket);

// ─────────────────────────────────────────────
// Auth anónima
// ─────────────────────────────────────────────
signInAnonymously(auth).catch((e) =>
  console.error("[auth] anon sign-in error:", e)
);

export const authReady = new Promise((resolve) => {
  const off = onAuthStateChanged(auth, () => {
    off();
    resolve(true);
  });
});

// ─────────────────────────────────────────────
// App Check (reCAPTCHA v3) con interruptor por .env
// ─────────────────────────────────────────────
const siteKey = (import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? "").trim();
const enableAppCheck =
  String(import.meta.env.VITE_ENABLE_APPCHECK ?? "true").toLowerCase() !== "false";

if (import.meta.env.DEV) {
  // @ts-ignore
  globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

let appCheckInstance = null;

try {
  if (enableAppCheck && siteKey) {
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    console.log("[AppCheck] habilitado");
  } else if (enableAppCheck && import.meta.env.DEV) {
    appCheckInstance = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider("debug"),
      isTokenAutoRefreshEnabled: true,
    });
    console.warn("[AppCheck] DEV con provider 'debug'");
  } else {
    console.warn("[AppCheck] desactivado (VITE_ENABLE_APPCHECK=false o sin siteKey)");
  }
} catch (e) {
  console.error("[AppCheck] fallo al iniciar:", e);
}

export const appCheckReady = appCheckInstance
  ? new Promise((resolve, reject) => {
      const off = onTokenChanged(
        appCheckInstance,
        (token) => {
          if (token) {
            off();
            resolve(true);
          }
        },
        (err) => {
          off();
          reject(err);
        }
      );
    })
  : Promise.resolve(true);

export async function ensureAppCheckToken() {
  if (!appCheckInstance) return;
  await getToken(appCheckInstance, true);
}
